import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Shield, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";

const EMPTY = { email: "", name: "", password: "", roles: [], department: "", employee_id: "" };

const ROLE_OPTIONS = [
    { value: "admin", label: "Admin", hint: "Full access — including user management" },
    { value: "hr", label: "HR Officer", hint: "Manage employees, departments, leaves (no user mgmt)" },
    { value: "department_lead", label: "Department Lead", hint: "Approve leaves for their department. If dept = Finance, can give Finance Lead approval on loans." },
    { value: "accountant", label: "Accountant", hint: "Verify loans, approve rent receipts, disburse loans" },
    { value: "finance_lead", label: "Finance Lead", hint: "Final approval on loans/advances (after Dept Lead)" },
    { value: "viewer", label: "Viewer", hint: "Read-only access (planned)" },
    { value: "hr_clerk", label: "HR Clerk", hint: "Adds and updates employee details only. Cannot see salaries, payroll or any pay information" },
    { value: "appraiser", label: "Performance Appraiser", hint: "Only Performance appraisals and Tasks. No other part of the app" },
    { value: "employee", label: "Employee", hint: "Self-service: own payslips, leave, loans and check-in (needs a linked employee record)" },
];

const roleLabel = (r) => ROLE_OPTIONS.find((o) => o.value === r)?.label || r;

export default function Users() {
    const { user: currentUser, checking } = useAuth();
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, d, em] = await Promise.all([
                api.get("/users"), api.get("/departments"), api.get("/employees").catch(() => ({ data: [] })),
            ]);
            setUsers(u.data);
            setDepartments(d.data);
            setEmployees(em.data || []);
        } catch (e) {
            console.error("Failed to load users:", e);
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Strict admin gate (sidebar already hides the link, but block direct URL access too)
    if (!checking && currentUser && !hasRole(currentUser, "admin")) {
        return <Navigate to="/dashboard" replace />;
    }

    const onNew = () => {
        setEditing(null);
        setForm(EMPTY);
        setShowPass(false);
        setOpen(true);
    };
    const onEdit = (u) => {
        setEditing(u);
        setForm({
            email: u.email, name: u.name, password: "", roles: u.roles?.length ? u.roles : [u.role],
            department: u.department || "", employee_id: u.employee_id || "",
        });
        setShowPass(false);
        setOpen(true);
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!form.roles.length) { toast.error("Pick at least one role"); return; }
        if (form.roles.includes("employee") && !form.employee_id) { toast.error("Link the employee record for the Employee role"); return; }
        setSaving(true);
        try {
            if (editing) {
                const payload = { name: form.name, roles: form.roles, department: form.department || "", employee_id: form.employee_id || "" };
                if (form.password) payload.password = form.password;
                await api.put(`/users/${editing.id}`, payload);
                toast.success("User updated");
            } else {
                await api.post("/users", form);
                toast.success("User created");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (u) => {
        if (u.id === currentUser?.id) {
            toast.error("You cannot delete your own account");
            return;
        }
        if (!window.confirm(`Delete admin user "${u.email}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Access control</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Admin users
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-lg">
                        Manage who can sign in to the HR portal. Department Leads can only approve
                        leave requests for employees in their department.
                    </p>
                </div>
                <button
                    onClick={onNew}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    data-testid="add-user-button"
                >
                    <Plus className="w-4 h-4" /> New user
                </button>
            </div>

            <div className="mt-12 bg-white border border-ink/10 overflow-auto max-h-[420px]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-ink/20">
                            <th className="text-left overline px-6 py-4">User</th>
                            <th className="text-left overline px-6 py-4">Role</th>
                            <th className="text-left overline px-6 py-4 hidden lg:table-cell">Created</th>
                            <th className="text-right overline px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" className="px-6 py-20 text-center text-sm text-ink/40">Loading…</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan="4" className="px-6 py-20 text-center text-sm text-ink/40">No users.</td></tr>
                        ) : (
                            users.map((u) => (
                                <tr key={u.id} className="border-b border-ink/10 hover:bg-bone/70" data-testid={`user-row-${u.id}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-klein/10 flex items-center justify-center font-display font-bold text-klein text-sm">
                                                {(u.name?.[0] || u.email?.[0] || "?").toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-ink flex items-center gap-2">
                                                    {u.name || "Unnamed"}
                                                    {u.id === currentUser?.id && (
                                                        <span className="text-[10px] uppercase tracking-[0.15em] border border-klein text-klein px-1.5 py-0.5">
                                                            You
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-ink/50">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1" data-testid={`user-role-${u.id}`}>
                                            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] bg-klein/8 border border-klein/30 text-klein px-2.5 py-1 font-semibold w-fit">
                                                <Shield className="w-3 h-3" /> {(u.roles?.length ? u.roles : [u.role]).map(roleLabel).join(" · ")}
                                            </span>
                                            {(u.roles || [u.role]).includes("department_lead") && u.department && (
                                                <span className="text-[11px] text-klein/80">→ {u.department}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-ink/60 hidden lg:table-cell">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => onEdit(u)}
                                                className="p-2 text-ink/60 hover:text-klein hover:bg-klein/5"
                                                data-testid={`user-edit-${u.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => onDelete(u)}
                                                disabled={u.id === currentUser?.id}
                                                className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5 disabled:opacity-30 disabled:cursor-not-allowed"
                                                data-testid={`user-delete-${u.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="user-dialog">
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-ink/10 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up"
                    >
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">{editing ? "Edit" : "New"}</div>
                                <h3 className="mt-1 font-display font-bold text-xl">{editing ? "Edit user" : "New user"}</h3>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5" data-testid="user-dialog-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Full name *</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="user-name-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">Email {editing ? "(read-only)" : "*"}</label>
                                <input
                                    required
                                    type="email"
                                    disabled={!!editing}
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none disabled:bg-ink/5 disabled:text-ink/60"
                                    data-testid="user-email-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">
                                    Password {editing ? "(leave blank to keep current)" : "*"}
                                </label>
                                <div className="relative">
                                    <input
                                        required={!editing}
                                        type={showPass ? "text" : "password"}
                                        minLength={editing ? 0 : 8}
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        placeholder={editing ? "•••••••• (unchanged)" : "Min 8 characters"}
                                        className="w-full border border-ink/15 py-2.5 px-3 pr-10 text-sm focus:border-klein focus:outline-none"
                                        data-testid="user-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPass((s) => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink/40 hover:text-ink"
                                        tabIndex={-1}
                                    >
                                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <RolePicker value={form.roles} onChange={(roles) => setForm({ ...form, roles })} />
                            {form.roles.includes("employee") && (
                                <div data-testid="user-employee-wrap">
                                    <label className="overline block mb-2">
                                        Linked employee record <span className="text-destructive">*</span>
                                    </label>
                                    <EmployeePicker employees={employees} value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} className="py-2.5" testid="user-employee-input" />
                                    <p className="text-xs text-ink/50 mt-2">Their own payslips, leave, loans and check-in come from this record.</p>
                                </div>
                            )}
                            {form.roles.includes("department_lead") && (
                                <div data-testid="user-department-wrap">
                                    <label className="overline block mb-2">
                                        Department <span className="text-destructive">*</span>
                                    </label>
                                    <select
                                        required
                                        value={form.department}
                                        onChange={(e) => setForm({ ...form, department: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm bg-white focus:border-klein focus:outline-none"
                                        data-testid="user-department-input"
                                    >
                                        <option value="">Select department</option>
                                        {departments.map((d) => (
                                            <option key={d.id} value={d.name}>{d.name}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-ink/50 mt-2">
                                        This user can only view and approve leave requests for employees in this department.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5" data-testid="user-cancel-button">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="user-save-button">
                                {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function RolePicker({ value, onChange }) {
    const toggle = (r) => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
    return (
        <div data-testid="user-roles-picker">
            <label className="overline block mb-2">Roles <span className="text-ink/40 normal-case tracking-normal">(pick one or more — access is combined)</span></label>
            <div className="border border-ink/15 divide-y divide-ink/10">
                {ROLE_OPTIONS.map((r) => (
                    <label key={r.value} className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-bone/60">
                        <input
                            type="checkbox"
                            checked={value.includes(r.value)}
                            onChange={() => toggle(r.value)}
                            className="mt-0.5 accent-klein"
                            data-testid={`user-role-checkbox-${r.value}`}
                        />
                        <span>
                            <span className="text-sm text-ink font-medium">{r.label}</span>
                            <span className="block text-xs text-ink/50">{r.hint}</span>
                        </span>
                    </label>
                ))}
            </div>
        </div>
    );
}
